import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'

/**
 * React Query wrapper around the sequencer project sharing
 * endpoints. Shape mirrors `useSharing.ts` for layouts so the
 * `ShareProjectDialog` can be a copy-paste sibling of
 * `ShareLayoutDialog` with only route strings + query keys swapped.
 */

export type ProjectShareStatus = 'active' | 'pending'

export interface ActiveProjectCollaborator {
  status: 'active'
  recipientUserId: string
  recipientEmail: string
  role: 'viewer' | 'editor'
  grantedAt: string
  expiresAt?: string
}

export interface PendingProjectCollaborator {
  status: 'pending'
  recipientEmail: string
  role: 'viewer' | 'editor'
  invitedAt: string
  expiresAt: string
}

export type ProjectCollaborator =
  | ActiveProjectCollaborator
  | PendingProjectCollaborator

interface CollaboratorsResponse {
  collaborators: ProjectCollaborator[]
}

interface ShareProjectResponse {
  status: ProjectShareStatus
  projectId: string
  recipientEmail: string
  role: 'viewer' | 'editor'
  recipientUserId?: string
  grantedAt?: string
  invitedAt?: string
  expiresAt?: string
}

export function useProjectCollaborators(
  projectId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['project-collaborators', projectId],
    queryFn: async () => {
      if (!projectId) return [] as ProjectCollaborator[]
      const response = await apiClient.get<CollaboratorsResponse>(
        `/api/v1/sequencer/containers/${projectId}/collaborators`,
      )
      return response.collaborators ?? []
    },
    enabled: enabled && !!projectId,
  })
}

export function useShareProject(projectId: string | null) {
  const queryClient = useQueryClient()
  return useMutation<
    ShareProjectResponse,
    Error,
    { email: string; role: 'viewer' | 'editor'; expiresAt?: string }
  >({
    mutationFn: async input => {
      if (!projectId) throw new Error('No projectId')
      return apiClient.post<ShareProjectResponse>(
        `/api/v1/sequencer/containers/${projectId}/share`,
        input,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['project-collaborators', projectId],
      })
      queryClient.invalidateQueries({ queryKey: ['sequencerProjects'] })
    },
  })
}

export function useUnshareProject(projectId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (recipientUserId: string) => {
      if (!projectId) throw new Error('No projectId')
      return apiClient.delete(
        `/api/v1/sequencer/containers/${projectId}/share/${recipientUserId}`,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['project-collaborators', projectId],
      })
      queryClient.invalidateQueries({ queryKey: ['sequencerProjects'] })
    },
  })
}

export function useRevokeProjectInvite(projectId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (email: string) => {
      if (!projectId) throw new Error('No projectId')
      return apiClient.delete(
        `/api/v1/sequencer/containers/${projectId}/invite/${encodeURIComponent(
          email,
        )}`,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['project-collaborators', projectId],
      })
      queryClient.invalidateQueries({ queryKey: ['sequencerProjects'] })
    },
  })
}
